import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { EmployeeAbsence } from "@/hooks/useEmployeeAbsences";

const schema = z.object({
  absence_type: z.enum(["vacation", "sick", "unpaid", "other"]),
  start_date: z.string().min(1, "Required"),
  end_date: z.string().min(1, "Required"),
  notes: z.string().nullable().optional(),
});

type FormData = z.infer<typeof schema>;

interface AbsenceFormProps {
  absence?: EmployeeAbsence;
  onSubmit: (data: FormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function AbsenceForm({
  absence,
  onSubmit,
  onCancel,
  isLoading,
}: AbsenceFormProps) {
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      absence_type: absence?.absence_type || "vacation",
      start_date: absence?.start_date || "",
      end_date: absence?.end_date || "",
      notes: absence?.notes || "",
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <Label>Type</Label>
        <Select
          value={form.watch("absence_type")}
          onValueChange={(v) =>
            form.setValue("absence_type", v as FormData["absence_type"])
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="vacation">Vacation</SelectItem>
            <SelectItem value="sick">Sick</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Start Date</Label>
          <DatePicker
            value={form.watch("start_date")}
            onChange={(v) =>
              form.setValue("start_date", v, { shouldValidate: true })
            }
          />
          {form.formState.errors.start_date && (
            <p className="text-xs text-destructive mt-1">
              {form.formState.errors.start_date.message}
            </p>
          )}
        </div>
        <div>
          <Label>End Date</Label>
          <DatePicker
            value={form.watch("end_date")}
            onChange={(v) =>
              form.setValue("end_date", v, { shouldValidate: true })
            }
          />
          {form.formState.errors.end_date && (
            <p className="text-xs text-destructive mt-1">
              {form.formState.errors.end_date.message}
            </p>
          )}
        </div>
      </div>
      <div>
        <Label>Notes</Label>
        <Textarea {...form.register("notes")} placeholder="Optional notes" />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          {absence ? "Update" : "Add"} Absence
        </Button>
      </div>
    </form>
  );
}
